import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, resolveImageSrc } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// Status flow + advance map. Cancelled is a side path available at any "active" stage.
const NEXT_STATUS = {
  placed: { label: "✅ Confirm stock", next: "confirming_stock" },
  confirming_stock: { label: "🛵 Mark picked up", next: "picked_up" },
  picked_up: { label: "🚚 Mark in transit", next: "in_transit" },
  in_transit: { label: "🏠 Mark delivered", next: "delivered" },
  shipped: { label: "🏠 Mark delivered", next: "delivered" },
  delivered: null,
  cancelled: null,
};

const STATUS_LABEL = {
  placed: "Order placed",
  confirming_stock: "Confirming stock",
  picked_up: "Picked up — on the way",
  in_transit: "In transit",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_COLOR = {
  placed: "bg-amber-100 text-amber-800 border-amber-200",
  confirming_stock: "bg-blue-100 text-blue-800 border-blue-200",
  picked_up: "bg-purple-100 text-purple-800 border-purple-200",
  in_transit: "bg-indigo-100 text-indigo-800 border-indigo-200",
  shipped: "bg-cyan-100 text-cyan-800 border-cyan-200",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

const ACTIVE = ["placed", "confirming_stock", "picked_up", "in_transit", "shipped"];

const buildPin = (emoji, color) => L.divIcon({
  className: "qc-admin-pin",
  html: `<div style="width:34px;height:34px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 10px rgba(0,0,0,0.25);border:2px solid white;"><span style="transform:rotate(45deg);font-size:16px;line-height:1">${emoji}</span></div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});
const userPin = buildPin("🏠", "#0F3D2E");
const riderPin = buildPin("🛵", "#C97B3C");

function FitAll({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 1) return;
    if (points.length === 1) { map.setView(points[0], 15); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
  }, [points, map]);
  return null;
}

/**
 * Admin Q-commerce orders panel.
 * - Polls /api/admin/orders every 10s
 * - Plays a sound when a new pending order arrives while admin is logged in
 * - Auto-pushes admin (rider) GPS every 60s while any active order exists
 * - Each order has a button to advance status: Confirm stock → Picked up → Delivered
 */
export default function AdminQCOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | delivered | cancelled | all
  const [busyId, setBusyId] = useState(null);
  const [courierForm, setCourierForm] = useState({}); // {oid: {name,tid,url,open}}
  const [adminCoords, setAdminCoords] = useState(null);
  const [adminLocStatus, setAdminLocStatus] = useState("idle"); // idle | tracking | denied | pushed
  const lastSeenIdsRef = useRef(new Set());
  const audioRef = useRef(null);
  const locPushTimerRef = useRef(null);
  const watchIdRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data || []);
      // Sound alert if a NEW pending order has arrived while we were already polling
      const incoming = (data || []).filter((o) => o.status === "placed").map((o) => o.id);
      if (lastSeenIdsRef.current.size > 0) {
        const fresh = incoming.filter((id) => !lastSeenIdsRef.current.has(id));
        if (fresh.length > 0) {
          try { audioRef.current && audioRef.current.play(); } catch { /* user gesture not yet given */ }
          toast.success(`🛒 ${fresh.length} new order${fresh.length > 1 ? "s" : ""}!`);
        }
      }
      lastSeenIdsRef.current = new Set(incoming);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  // Auto location tracking: starts geolocation watch + pushes server every 60s
  // whenever there is at least one active order.
  const activeCount = useMemo(() => orders.filter((o) => ACTIVE.includes(o.status)).length, [orders]);

  useEffect(() => {
    if (activeCount === 0) {
      // Stop watch + timer if no active orders
      if (watchIdRef.current != null && navigator?.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (locPushTimerRef.current) { clearInterval(locPushTimerRef.current); locPushTimerRef.current = null; }
      setAdminLocStatus("idle");
      return;
    }
    // Start GPS watch
    if (!navigator?.geolocation) { setAdminLocStatus("denied"); return; }
    if (watchIdRef.current == null) {
      setAdminLocStatus("tracking");
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => setAdminCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setAdminLocStatus("denied"),
        { enableHighAccuracy: true, maximumAge: 30000, timeout: 20000 }
      );
    }
    // Push to server every 60s
    const push = async () => {
      if (!adminCoords) return;
      try {
        await api.post("/admin/location", { lat: adminCoords.lat, lng: adminCoords.lng });
        setAdminLocStatus("pushed");
      } catch { /* silent */ }
    };
    push(); // push immediately when we get coords
    if (locPushTimerRef.current) clearInterval(locPushTimerRef.current);
    locPushTimerRef.current = setInterval(push, 60000);
    return () => { if (locPushTimerRef.current) clearInterval(locPushTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCount, adminCoords?.lat, adminCoords?.lng]);

  const advance = async (order, target) => {
    setBusyId(order.id);
    try {
      await api.post(`/admin/orders/${order.id}/status`, { status: target });
      toast.success(`Updated to ${STATUS_LABEL[target]}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusyId(null); }
  };

  const setFulfillment = async (order, method, extra = {}) => {
    setBusyId(order.id);
    try {
      await api.post(`/orders/${order.id}/fulfillment`, { method, ...extra });
      toast.success(method === "courier" ? "Marked shipped ✓" : method === "delivery_partner" ? "Listed for delivery partners 🛵" : "Manual fulfillment set ✍️");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set fulfillment");
    } finally { setBusyId(null); }
  };
  const cf = (oid) => courierForm[oid] || {};
  const setCf = (oid, patch) => setCourierForm((f) => ({ ...f, [oid]: { ...(f[oid] || {}), ...patch } }));

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "active") return orders.filter((o) => ACTIVE.includes(o.status));
    if (filter === "delivered") return orders.filter((o) => o.status === "delivered");
    if (filter === "cancelled") return orders.filter((o) => o.status === "cancelled");
    return orders;
  }, [orders, filter]);

  return (
    <div data-testid="admin-orders-panel" className="space-y-4">
      {/* Hidden notification beep — short, royalty free, base64 (≈0.4s sine tone) */}
      <audio ref={audioRef} preload="auto" src="data:audio/wav;base64,UklGRoQGAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YWAGAAB7/4f/lf+i/7L/wP/N/9z/6f/4/wIAEgAfACwAOABCAFAAWwBkAGwAcgB3AHwAfwCBAIIAgQB+AHsAdwBxAGsAYgBaAFAARgA6AC8AIwAVAAgA+/8=" />
      <Card className="rounded-2xl border-slate-100">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Quick-Commerce Orders</h3>
              <p className="text-xs text-slate-500 mt-0.5">Buy Now (COD) orders from users. Auto-refreshes every 10s.</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div data-testid="admin-loc-status" className={`px-3 py-1.5 rounded-full border font-semibold ${
                adminLocStatus === "pushed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                adminLocStatus === "tracking" ? "bg-amber-50 text-amber-700 border-amber-200" :
                adminLocStatus === "denied" ? "bg-rose-50 text-rose-700 border-rose-200" :
                "bg-slate-50 text-slate-600 border-slate-200"
              }`}>
                {adminLocStatus === "pushed" && `📍 Live location ON ${adminCoords ? `(${adminCoords.lat.toFixed(3)}, ${adminCoords.lng.toFixed(3)})` : ""}`}
                {adminLocStatus === "tracking" && "📍 Getting location…"}
                {adminLocStatus === "denied" && "⚠️ Location blocked"}
                {adminLocStatus === "idle" && "📍 No active orders"}
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-2 flex-wrap">
            {[
              { k: "active", l: `Active (${orders.filter((o) => ACTIVE.includes(o.status)).length})` },
              { k: "delivered", l: `Delivered (${orders.filter((o) => o.status === "delivered").length})` },
              { k: "cancelled", l: `Cancelled (${orders.filter((o) => o.status === "cancelled").length})` },
              { k: "all", l: `All (${orders.length})` },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`admin-orders-filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === f.k ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
              >{f.l}</button>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center text-slate-500 py-6">Loading orders…</div>}

      {!loading && filtered.length === 0 && (
        <Card className="rounded-2xl border-slate-100"><CardContent className="p-8 text-center text-slate-500">
          <div className="text-4xl mb-2">📭</div>
          <div className="font-semibold text-slate-700">No orders here yet.</div>
          <div className="text-xs mt-1">When users place Buy Now orders, they'll show up here in real-time.</div>
        </CardContent></Card>
      )}

      <div className="grid gap-3" data-testid="admin-orders-list">
        {filtered.map((o) => {
          const action = NEXT_STATUS[o.status];
          const img = resolveImageSrc(o.product_image);
          const points = [];
          if (o.delivery_lat != null && o.delivery_lng != null) points.push([o.delivery_lat, o.delivery_lng]);
          if (o.delivery_lat_current != null && o.delivery_lng_current != null) points.push([o.delivery_lat_current, o.delivery_lng_current]);
          return (
            <Card key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-2xl border-slate-100">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  {img && (img.startsWith("http") || img.startsWith("data:")) && (
                    <img src={img} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{o.product_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{o.shop_name} · Qty {o.qty} · <span className="font-bold text-emerald-700">₹{o.total} COD</span></div>
                      </div>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLOR[o.status]} whitespace-nowrap`} data-testid={`admin-order-status-${o.id}`}>{STATUS_LABEL[o.status]}</Badge>
                    </div>

                    <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                      <div>👤 <strong className="text-slate-800">{o.user_name}</strong>{o.user_mobile ? <> · 📞 <a className="text-emerald-700 hover:underline" href={`tel:${o.user_mobile}`}>{o.user_mobile}</a></> : null}</div>
                      <div>📍 <span className="text-slate-700">{o.delivery_address}</span></div>
                      {o.note && <div className="italic text-slate-500">📝 {o.note}</div>}
                      <div className="text-slate-400">⏱ {new Date(o.created_at).toLocaleString()}</div>
                    </div>

                    {/* Map preview for active orders */}
                    {ACTIVE.includes(o.status) && points.length > 0 && (
                      <div className="mt-3 rounded-lg overflow-hidden border border-slate-200" data-testid={`admin-order-map-${o.id}`}>
                        <MapContainer center={points[0]} zoom={14} style={{ height: 160, width: "100%" }} attributionControl={false} dragging={false} doubleClickZoom={false} scrollWheelZoom={false} zoomControl={false}>
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          <FitAll points={points} />
                          <Marker position={[o.delivery_lat, o.delivery_lng]} icon={userPin} />
                          {o.delivery_lat_current != null && (
                            <>
                              <Marker position={[o.delivery_lat_current, o.delivery_lng_current]} icon={riderPin} />
                              <Polyline positions={[[o.delivery_lat_current, o.delivery_lng_current], [o.delivery_lat, o.delivery_lng]]} pathOptions={{ color: "#C97B3C", weight: 3, dashArray: "6 5", opacity: 0.85 }} />
                            </>
                          )}
                        </MapContainer>
                      </div>
                    )}

                    {/* Fulfillment method picker */}
                    {!["delivered", "cancelled"].includes(o.status) && (
                      <div className="mt-3" data-testid={`admin-fulfillment-${o.id}`}>
                        {!o.fulfillment_method ? (
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                            <div className="text-[11px] font-semibold text-slate-500 mb-1.5">How will this be fulfilled?</div>
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" data-testid={`admin-fulfil-partner-${o.id}`} disabled={busyId === o.id} onClick={() => setFulfillment(o, "delivery_partner")} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white">🛵 Delivery partner</Button>
                              <Button size="sm" variant="outline" data-testid={`admin-fulfil-courier-${o.id}`} onClick={() => setCf(o.id, { open: !cf(o.id).open })} className="rounded-full">📦 Courier</Button>
                              <Button size="sm" variant="outline" data-testid={`admin-fulfil-manual-${o.id}`} disabled={busyId === o.id} onClick={() => setFulfillment(o, "manual")} className="rounded-full">✍️ Manual</Button>
                            </div>
                            {cf(o.id).open && (
                              <div className="mt-2 flex flex-col gap-2" data-testid={`admin-courier-form-${o.id}`}>
                                <input data-testid={`admin-courier-name-${o.id}`} placeholder="Courier name (e.g. Delhivery)" value={cf(o.id).name || ""} onChange={(e) => setCf(o.id, { name: e.target.value })} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                                <input data-testid={`admin-courier-tid-${o.id}`} placeholder="Tracking ID" value={cf(o.id).tid || ""} onChange={(e) => setCf(o.id, { tid: e.target.value })} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                                <input data-testid={`admin-courier-url-${o.id}`} placeholder="Tracking URL (optional)" value={cf(o.id).url || ""} onChange={(e) => setCf(o.id, { url: e.target.value })} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                                <Button size="sm" data-testid={`admin-courier-submit-${o.id}`} disabled={busyId === o.id} onClick={() => setFulfillment(o, "courier", { courier_name: cf(o.id).name, tracking_id: cf(o.id).tid, tracking_url: cf(o.id).url })} className="rounded-full bg-emerald-700 text-white self-start">Save & mark shipped</Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-[11px] font-semibold text-slate-600" data-testid={`admin-fulfillment-badge-${o.id}`}>
                            {o.fulfillment_method === "delivery_partner" && (o.delivery_agent_name ? `🛵 Partner: ${o.delivery_agent_name}` : "🛵 Listed for delivery partners")}
                            {o.fulfillment_method === "courier" && `📦 ${o.courier_name} · ${o.tracking_id}`}
                            {o.fulfillment_method === "manual" && "✍️ Manual fulfillment"}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions — manual/courier advance (delivery-partner orders advance themselves) */}
                    {action && o.fulfillment_method && o.fulfillment_method !== "delivery_partner" && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          data-testid={`admin-order-advance-${o.id}`}
                          disabled={busyId === o.id}
                          onClick={() => advance(o, action.next)}
                          className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                        >{action.label}</Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`admin-order-cancel-${o.id}`}
                          disabled={busyId === o.id}
                          onClick={() => advance(o, "cancelled")}
                          className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
                        >Cancel</Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
